import type { CanvasKit, GrDirectContext, Surface } from 'canvaskit-wasm';
import CanvasKitInit from 'canvaskit-wasm';
import wasmUrl from 'canvaskit-wasm/bin/canvaskit.wasm?url';

let loading: Promise<CanvasKit> | null = null;

/**
 * Loads CanvasKit once per page. The engine is 7.3 MB raw, about 2.3 MB compressed.
 *
 * A load that FAILED is forgotten, so the next file opened tries again: kept, one network
 * blip left every file in the tab blank until a reload (QA 2026-09-24, files-doc-F8).
 */
export function loadCanvasKit(): Promise<CanvasKit> {
  loading ??= CanvasKitInit({ locateFile: () => wasmUrl }).catch((err: unknown) => {
    loading = null;
    throw err;
  });
  return loading;
}

/**
 * A WebGL surface that survives resizes. One GrDirectContext is kept for the life of the
 * canvas, so cached tile textures stay valid when the window changes size; only the
 * on-screen surface is recreated.
 *
 * It survives the GPU going away too. The browser takes the context back when the machine is
 * short of memory or the graphics process restarts, and hands it back only to a page that
 * said it wanted it: unasked, the canvas stayed black until a reload (1 Nov).
 */
export class GlSurface {
  private context!: GrDirectContext;
  /** CanvasKit's handle for the WebGL context, which its global table holds until deleted. */
  private handle = 0;
  private current: Surface | null = null;
  private size = { width: 0, height: 0 };
  private gone = false;
  /** Told when the GPU is back: everything that lived on the old context has to be made again. */
  onRestored: (() => void) | null = null;

  constructor(
    private readonly ck: CanvasKit,
    private readonly canvas: HTMLCanvasElement,
  ) {
    this.connect();
    canvas.addEventListener('webglcontextlost', this.handleLost);
    canvas.addEventListener('webglcontextrestored', this.handleRestored);
  }

  /** The GPU has gone: nothing can be drawn until it is back, so a frame has nothing to do. */
  get lost(): boolean {
    return this.gone;
  }

  private readonly handleLost = (event: Event) => {
    // Without this the browser never gives the context back.
    event.preventDefault();
    this.gone = true;
    this.current?.delete();
    this.current = null;
    // Abandoned now, so Skia makes no more calls into a context that no longer exists.
    this.context.releaseResourcesAndAbandonContext();
  };

  private readonly handleRestored = () => {
    this.context.delete();
    this.ck.deleteContext(this.handle);
    this.connect();
    this.size = { width: 0, height: 0 };
    this.gone = false;
    this.onRestored?.();
  };

  private connect(): void {
    const { ck, canvas } = this;
    const handle = ck.GetWebGLContext(canvas, {
      majorVersion: 2,
      alpha: 0,
      antialias: 0,
      depth: 0,
      stencil: 0,
      premultipliedAlpha: 1,
      // We draw on demand. Keep the last frame available when Chrome recomposites
      // glass panels over an idle canvas rather than relying on a discarded buffer.
      preserveDrawingBuffer: 1,
      enableExtensionsByDefault: 1,
    });
    const context = ck.MakeWebGLContext(handle);
    if (!context) throw new Error('WebGL 2 is not available in this browser.');
    context.setResourceCacheLimitBytes(512 * 1024 * 1024);
    this.context = context;
    this.handle = handle;
  }

  /** The surface for a CSS size and device pixel ratio, recreated only when the pixel size changes. */
  surfaceFor(cssWidth: number, cssHeight: number, dpr: number): Surface {
    const width = Math.max(1, Math.round(cssWidth * dpr));
    const height = Math.max(1, Math.round(cssHeight * dpr));
    if (this.current && width === this.size.width && height === this.size.height) return this.current;
    this.canvas.width = width;
    this.canvas.height = height;
    this.current?.delete();
    const surface = this.ck.MakeOnScreenGLSurface(this.context, width, height, this.ck.ColorSpace.SRGB);
    if (!surface) throw new Error('Could not create a WebGL surface.');
    this.current = surface;
    this.size = { width, height };
    return surface;
  }

  /**
   * A GPU surface of its own on the canvas's context, for a small drawing made away from the
   * screen (the Effects tiles). Null when the context will not make one; the caller falls
   * back to a CPU surface.
   */
  offscreen(width: number, height: number): Surface | null {
    return this.ck.MakeRenderTarget(this.context, width, height);
  }

  dispose(): void {
    this.canvas.removeEventListener('webglcontextlost', this.handleLost);
    this.canvas.removeEventListener('webglcontextrestored', this.handleRestored);
    this.current?.delete();
    this.current = null;
    this.context.releaseResourcesAndAbandonContext();
    /**
     * Abandoning frees Skia's GPU objects but not the context itself: CanvasKit's global
     * context table kept every file's canvas and WebGL context alive, so each file opened
     * and left in a tab held a full-window buffer until the browser warned about too many
     * contexts (QA 2026-09-24, files-doc-F15). The context is not LOST on purpose — the
     * same <canvas> is reused for the next file, and a lost context cannot be got back.
     */
    this.context.delete();
    this.ck.deleteContext(this.handle);
  }
}
